import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Trash2, Pencil, Plus } from "lucide-react";
import { toast } from "sonner";

const blank = { title: "", destination: "", nights: 5, price: 0, image: "", inclusions: "", active: true };

const toInclArray = (s) => Array.isArray(s) ? s : String(s || "").split(",").map(x => x.trim()).filter(Boolean);
const toInclString = (a) => Array.isArray(a) ? a.join(", ") : (a || "");

export default function AdminPackages() {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = async () => { const { data } = await api.get("/admin/packages"); setRows(data); };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!edit.title || !edit.price) return toast.error("Title & price required");
    const payload = { ...edit, price: Number(edit.price), nights: Number(edit.nights), inclusions: toInclArray(edit.inclusions) };
    try {
      if (edit.id) await api.patch(`/admin/packages/${edit.id}`, payload);
      else await api.post("/admin/packages", payload);
      toast.success("Saved"); setEdit(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const inlineToggle = async (id, active) => { await api.patch(`/admin/packages/${id}`, { active }); load(); };
  const remove = async (id) => { if (!window.confirm("Delete this package?")) return; await api.delete(`/admin/packages/${id}`); load(); };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div><h1 className="font-heading text-3xl font-semibold text-[var(--brand-accent)]">Holiday packages</h1><p className="text-slate-500 mt-1">Manage holiday bundles displayed on the site.</p></div>
        <Button data-testid="pk-new" onClick={() => setEdit({ ...blank })} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] rounded-full"><Plus className="w-4 h-4 mr-1" /> New package</Button>
      </div>

      <Card className="mt-5 overflow-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Image</TableHead><TableHead>Title</TableHead><TableHead>Destination</TableHead><TableHead>Nights</TableHead><TableHead>Price</TableHead><TableHead>Active</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.map(r => (
              <TableRow key={r.id} data-testid={`pk-row-${r.id}`}>
                <TableCell>{r.image && <img src={r.image} alt="" className="w-16 h-10 object-cover rounded" />}</TableCell>
                <TableCell className="font-medium">{r.title}</TableCell>
                <TableCell>{r.destination}</TableCell>
                <TableCell>{r.nights}</TableCell>
                <TableCell>${Number(r.price).toFixed(0)}</TableCell>
                <TableCell><Switch checked={r.active !== false} onCheckedChange={v => inlineToggle(r.id, v)} /></TableCell>
                <TableCell className="flex gap-2">
                  <Button data-testid={`pk-edit-${r.id}`} size="sm" variant="outline" onClick={() => setEdit({ ...r, inclusions: toInclString(r.inclusions) })}><Pencil className="w-3.5 h-3.5 mr-1" /> Edit</Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(r.id)} className="text-red-600"><Trash2 className="w-3.5 h-3.5" /></Button>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-10">No packages</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        {edit && (
          <DialogContent>
            <DialogHeader><DialogTitle className="font-heading text-xl text-[var(--brand-accent)]">{edit.id ? "Edit package" : "New package"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Title</Label><Input value={edit.title || ""} onChange={e=>setEdit({...edit,title:e.target.value})}/></div>
              <div className="col-span-2"><Label>Destination</Label><Input value={edit.destination || ""} onChange={e=>setEdit({...edit,destination:e.target.value})}/></div>
              <div><Label>Nights</Label><Input type="number" value={edit.nights || 0} onChange={e=>setEdit({...edit,nights:e.target.value})}/></div>
              <div><Label>Price ($)</Label><Input type="number" value={edit.price || 0} onChange={e=>setEdit({...edit,price:e.target.value})}/></div>
              <div className="col-span-2"><Label>Image URL</Label><Input value={edit.image || ""} onChange={e=>setEdit({...edit,image:e.target.value})}/></div>
              <div className="col-span-2"><Label>Inclusions (comma-separated)</Label><Input value={edit.inclusions || ""} onChange={e=>setEdit({...edit,inclusions:e.target.value})}/></div>
              <div className="col-span-2 flex items-center justify-between"><Label>Active</Label><Switch checked={edit.active !== false} onCheckedChange={v=>setEdit({...edit,active:v})}/></div>
            </div>
            <DialogFooter><Button variant="ghost" onClick={() => setEdit(null)}>Cancel</Button><Button data-testid="pk-save" onClick={submit} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)]">{edit.id ? "Save" : "Create"}</Button></DialogFooter>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
}
